import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle2, Circle, Clock, Inbox, PlayCircle, XCircle } from "lucide-react";

interface IssueStatusIconProps {
  status: string;
  className?: string;
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
  "data-testid"?: string;
}

export function IssueStatusIcon({
  status,
  className,
  showLabel = false,
  size = "md",
  "data-testid": dataTestId,
}: IssueStatusIconProps) {
  const getStatusConfig = (status: string) => {
    const normalizedStatus = status.toLowerCase().replace(/[_-]/g, " ");

    switch (normalizedStatus) {
      case "triage":
        return {
          icon: Inbox,
          label: "Triage",
          colorClass: "text-orange-500 dark:text-orange-400",
          bgClass: "bg-orange-100 dark:bg-orange-950/50",
        };
      case "backlog":
        return {
          icon: Circle,
          label: "Backlog",
          colorClass: "text-slate-400 dark:text-slate-500",
          bgClass: "bg-slate-100 dark:bg-slate-800/50",
        };
      case "todo":
        return {
          icon: Circle,
          label: "To Do",
          colorClass: "text-blue-500 dark:text-blue-400",
          bgClass: "bg-blue-100 dark:bg-blue-950/50",
        };
      case "in progress":
        return {
          icon: PlayCircle,
          label: "In Progress",
          colorClass: "text-amber-500 dark:text-amber-400",
          bgClass: "bg-amber-100 dark:bg-amber-950/50",
        };
      case "in review":
        return {
          icon: Clock,
          label: "In Review",
          colorClass: "text-violet-500 dark:text-violet-400",
          bgClass: "bg-violet-100 dark:bg-violet-950/50",
        };
      case "done":
        return {
          icon: CheckCircle2,
          label: "Done",
          colorClass: "text-emerald-500 dark:text-emerald-400",
          bgClass: "bg-emerald-100 dark:bg-emerald-950/50",
        };
      case "canceled":
        return {
          icon: XCircle,
          label: "Canceled",
          colorClass: "text-red-500 dark:text-red-400",
          bgClass: "bg-red-100 dark:bg-red-950/50",
        };
      default:
        return {
          icon: AlertCircle,
          label: status,
          colorClass: "text-muted-foreground",
          bgClass: "bg-muted",
        };
    }
  };

  const sizeConfig = {
    sm: {
      icon: "h-3.5 w-3.5",
      text: "text-xs",
      padding: "px-1.5 py-0.5",
    },
    md: {
      icon: "h-4 w-4",
      text: "text-sm",
      padding: "px-2 py-1",
    },
    lg: {
      icon: "h-5 w-5",
      text: "text-base",
      padding: "px-2.5 py-1.5",
    },
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;
  const sizes = sizeConfig[size];

  if (showLabel) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full font-medium",
          sizes.padding,
          sizes.text,
          config.bgClass,
          className
        )}
        data-testid={dataTestId}
      >
        <Icon className={cn(sizes.icon, config.colorClass)} />
        <span className={config.colorClass}>{config.label}</span>
      </div>
    );
  }

  return <Icon className={cn(sizes.icon, config.colorClass, className)} data-testid={dataTestId} />;
}

export default IssueStatusIcon;
